import React, { useEffect, useState } from 'react';

import { appsApi } from '../../api';
import { useAlerts } from '../context';
import Loader from '../common/Loader';
import { KYC_ADDED_OPTIONS, KYC_STANDALONE_OPTION } from '../../constants';
import KYCFlowForm from './KYCFlowForm';
import ConfirmationModal from '../common/ConfirmationModal';
import KYCFlowList from './KYCFlowList';

export default function ConsoleKYCFlow({ app, t }) {
  const { handle, frozen } = app;
  const [isEditing, setIsEditing] = useState(false);
  const [isCreate, setIsCreate] = useState(false);

  const [options, setOptions] = useState({});
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [flows, setFlows] = useState({});
  const [selectedFlow, setSelectedFlow] = useState({});
  const [flowToSubmit, setFlowToSubmit] = useState('');

  const { setAlert } = useAlerts();

  const clearFlow = () => {
    setIsCreate(false);
    setSelectedFlow({});
  };

  const handleSelectFlow = (flow) => {
    setIsCreate(false);
    setIsEditing(false);
    setSelectedFlow(flow);
  };

  const setCreateFlow = (newVersion, flow) => {
    setIsCreate(true);
    setIsEditing(true);
    const newFlow = {
      name: '',
      version: 1,
      selected_standalone_option: null,
      selected_additional_options: [],
    };
    if (newVersion) {
      newFlow.name = flow.name;
      newFlow.version = flow.next_version;
      newFlow.selected_standalone_option = flow.selected_standalone_option;
      newFlow.selected_additional_options = flow.selected_additional_options;
      newFlow.isNext = true;
    }
    setSelectedFlow(newFlow);
  };

  const createOrUpdateFlow = async (values) => {
    const selectedOptions = values[KYC_ADDED_OPTIONS];
    selectedOptions.push(values[KYC_STANDALONE_OPTION]);

    let request = () => appsApi.updateKycFlow(values.name, handle, selectedOptions, values.uuid);

    if (isCreate) {
      request = () => appsApi.createKycFlow(values.name, handle, selectedOptions, values.isNext);
    }

    try {
      const response = await request();
      const { flow, flow_tree } = response;
      const updatedFlows = { ...flows };
      updatedFlows[flow.uuid] = flow;
      if (isCreate && flow_tree.length) {
        flow_tree.forEach((treeFlow) => {
          updatedFlows[treeFlow].next_version += 1;
        });
      }
      setFlows(updatedFlows);
      handleSelectFlow(flow);
      setAlert({ message: response.message, variant: 'success' });
    } catch (error) {
      setAlert({ message: error, variant: 'warning' });
    }
  };

  const deleteFlow = async (uuid) => {
    try {
      const response = await appsApi.deleteFlow(handle, uuid);
      if (response.success) setAlert({ message: response.message, variant: 'success' });
      const flowsClone = { ...flows };
      delete flowsClone[uuid];
      setFlows(flowsClone);
      clearFlow();
      setAlert({ message: response.message, variant: 'success' });
    } catch (error) {
      setAlert({ message: error, variant: 'warning' });
    }
  };

  const submitKycFlowForApproval = async (uuid) => {
    try {
      const response = await appsApi.submitKycFlowForApproval(handle, uuid);
      const { flow } = response;
      const updatedFlows = { ...flows };
      updatedFlows[flow.uuid] = flow;
      setFlows(updatedFlows);
      setAlert({ message: response.message, variant: 'success' });
      if (flow.uuid === selectedFlow.uuid) {
        clearFlow();
      }
    } catch (error) {
      setAlert({ message: error, variant: 'warning' });
    }
    setFlowToSubmit('');
  };

  useEffect(() => {
    const fetchKYCOptions = async () => {
      try {
        const response = await appsApi.fetchKYCFlowOptions(handle);
        setOptions(response.options);
      } catch (error) {
        setAlert({ message: error, variant: 'warning' });
      }
      setOptionsLoaded(true);
    };

    fetchKYCOptions();
  }, []);

  useEffect(() => {
    const getFlows = async () => {
      setLoaded(false);
      try {
        const response = await appsApi.fetchKycFlows(handle);
        setFlows(response.kyc_flows);
      } catch (error) {
        setAlert({ message: error, variant: 'warning' });
      }
      setLoaded(true);
    };

    getFlows();
  }, []);

  if (!loaded && !optionsLoaded) return <Loader />;

  return (
    <>
      <KYCFlowForm
        flow={selectedFlow}
        clear={clearFlow}
        options={options}
        setCreateFlow={setCreateFlow}
        appHandle={handle}
        editFlow={createOrUpdateFlow}
        deleteFlow={deleteFlow}
        setIsEditing={setIsEditing}
        isEditing={isEditing}
        isCreate={isCreate}
        readonly={frozen}
      />

      <KYCFlowList
        flows={flows}
        t={t}
        handleSelectFlow={handleSelectFlow}
        setFlowToSubmit={setFlowToSubmit}
        readonly={frozen}
      />

      {flowToSubmit && (
        <ConfirmationModal
          show={flowToSubmit !== ''}
          title="Submit Flow for approval"
          message={t('applications.kycFlows.requestApproval')}
          buttonLabel={t('common:buttons.submit')}
          buttonLoadingLabel={t('common:status.submitting')}
          buttonVariant="warning"
          onHide={() => setFlowToSubmit('')}
          handleSubmit={() => submitKycFlowForApproval(flowToSubmit)}
          setShow={setFlowToSubmit}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
        />
      )}
    </>
  );
}
